import maplibregl, { addProtocol, removeProtocol } from 'maplibre-gl'
import SunExposureWorker from './sun-exposure-worker.ts?worker'
import SunCalc from 'suncalc'
import { timelineService, type TimelineState } from '../timeline/timeline-service.js'
import { addOverlayLayer } from '../../components/map/layer-order-manager.js'
import { demTileCache } from './dem-tile-cache.js'
import { renderSunExposureTileGPU } from './sun-exposure-gpu.js'
import { logger } from '../../utils/logger.js'
import { DEM_TILE_SIZE, DEM_MAX_ZOOM } from './dem-config.js'

const log = logger.create('sun-exposure')
const PROTOCOL = 'sun'
const TILE_SIZE = DEM_TILE_SIZE
const PADDED_SIZE = TILE_SIZE * 3
const WORKER_POOL_SIZE = 4
const DEFAULT_OPACITY = 0.7
const TIME_QUANTIZE_MS = 30 * 60 * 1000 // 30 minutes

// Two alternating buffer slots for double-buffering
const BUFFERS = [
  { sourceId: 'sun-exposure-source-a', layerId: 'sun-exposure-layer-a' },
  { sourceId: 'sun-exposure-source-b', layerId: 'sun-exposure-layer-b' },
] as const

function quantizeTime(ms: number): number {
  return Math.floor(ms / TIME_QUANTIZE_MS) * TIME_QUANTIZE_MS
}

// Convert tile coordinates to center lat/lng
function tileCenterLatLng(x: number, y: number, z: number): { lat: number; lng: number } {
  const n = Math.pow(2, z)
  const lng = ((x + 0.5) / n) * 360 - 180
  const latRad = Math.atan(Math.sinh(Math.PI * (1 - (2 * (y + 0.5)) / n)))
  const lat = (latRad * 180) / Math.PI
  return { lat, lng }
}

export type SunExposureMode = 'gradient' | 'binary'

class SunExposureService {
  private workers: Worker[] = []
  private workerIndex = 0
  private map: maplibregl.Map | null = null
  private _enabled = false
  private _opacity = DEFAULT_OPACITY
  private _mode: SunExposureMode = 'gradient'
  private pendingRequests = new Map<string, AbortController>()
  private currentTimeMs = 0
  private lastRefreshTime = 0
  private pendingRefreshTimer: ReturnType<typeof setTimeout> | null = null
  private static readonly REFRESH_INTERVAL_MS = 800

  // Double-buffer state
  private frontBuffer = 0 // index into BUFFERS: which is currently visible
  private backBufferLoading = false
  private refreshGeneration = 0 // guards deferred buffer removal against stale callbacks
  private sourceLoadHandler: ((e: { sourceId: string }) => void) | null = null

  private nextRequestId = 0

  get enabled(): boolean {
    return this._enabled
  }
  get opacity(): number {
    return this._opacity
  }
  get mode(): SunExposureMode {
    return this._mode
  }

  setMode(mode: SunExposureMode): void {
    if (mode === this._mode) {
      return
    }
    this._mode = mode
    if (this._enabled && this.map) {
      // Force full refresh with new mode
      this.doRefresh()
    }
  }

  private initWorkers(): void {
    if (this.workers.length > 0) {
      return
    }
    for (let i = 0; i < WORKER_POOL_SIZE; i++) {
      this.workers.push(new SunExposureWorker())
    }
  }

  private getWorker(): Worker {
    const worker = this.workers[this.workerIndex]
    this.workerIndex = (this.workerIndex + 1) % this.workers.length
    return worker
  }

  private terminateWorkers(): void {
    this.workers.forEach(w => w.terminate())
    this.workers = []
    this.workerIndex = 0
  }

  register(map: maplibregl.Map): void {
    this.map = map
    addProtocol(PROTOCOL, (params, abortController) => {
      return this.handleTileRequest(params, abortController)
    })
  }

  unregister(): void {
    this.disable()
    try {
      removeProtocol(PROTOCOL)
    } catch (_) {
      /* may not be registered */
    }
    this.terminateWorkers()
    this.map = null
  }

  enable(): void {
    if (!this.map || this._enabled) {
      return
    }
    this._enabled = true

    this.currentTimeMs = quantizeTime(timelineService.getState().currentTime.getTime())
    this.frontBuffer = 0
    this.backBufferLoading = false

    // Add the initial front buffer invisibly, reveal once all tiles are loaded
    this.addBuffer(0, this.currentTimeMs, 0)
    this.awaitBufferLoaded(0, () => {
      if (!this._enabled || !this.map) return
      const { layerId } = BUFFERS[0]
      if (this.map.getLayer(layerId)) {
        this.map.setPaintProperty(layerId, 'raster-opacity', this._opacity)
      }
    })

    // Subscribe to timeline changes
    timelineService.addListener(this.onTimelineChange)
  }

  disable(): void {
    if (!this.map) {
      return
    }
    this._enabled = false

    timelineService.removeListener(this.onTimelineChange)

    if (this.pendingRefreshTimer !== null) {
      clearTimeout(this.pendingRefreshTimer)
      this.pendingRefreshTimer = null
    }

    if (this.sourceLoadHandler) {
      this.map.off('sourcedata', this.sourceLoadHandler)
      this.sourceLoadHandler = null
    }

    for (const [, controller] of this.pendingRequests) {
      controller.abort()
    }
    this.pendingRequests.clear()

    this.removeBuffer(0)
    this.removeBuffer(1)
    this.terminateWorkers()
  }

  toggle(): void {
    if (this._enabled) {
      this.disable()
    } else {
      this.enable()
    }
  }

  reAdd(): void {
    if (!this._enabled || !this.map) {
      return
    }
    // Re-initialize after a style change wiped all layers/sources
    this.currentTimeMs = quantizeTime(timelineService.getState().currentTime.getTime())
    this.frontBuffer = 0
    this.backBufferLoading = false
    this.addBuffer(0, this.currentTimeMs, 0)
    this.awaitBufferLoaded(0, () => {
      if (!this._enabled || !this.map) return
      const { layerId } = BUFFERS[0]
      if (this.map.getLayer(layerId)) {
        this.map.setPaintProperty(layerId, 'raster-opacity', this._opacity)
      }
    })
  }

  setOpacity(opacity: number): void {
    this._opacity = Math.max(0, Math.min(1, opacity))
    // Update whichever buffer is currently visible
    const front = BUFFERS[this.frontBuffer]
    if (this.map?.getLayer(front.layerId)) {
      this.map.setPaintProperty(front.layerId, 'raster-opacity', this._opacity)
    }
  }

  private addBuffer(index: number, timeMs: number, opacity: number): void {
    if (!this.map) {
      return
    }
    const { sourceId, layerId } = BUFFERS[index]

    if (!this.map.getSource(sourceId)) {
      this.map.addSource(sourceId, {
        type: 'raster',
        tiles: [`${PROTOCOL}://{z}/{x}/{y}?t=${timeMs}&m=${this._mode}`],
        tileSize: TILE_SIZE,
        minzoom: 12,
        maxzoom: DEM_MAX_ZOOM,
        attribution: 'Elevation: Mapterhorn',
      })
    }

    if (!this.map.getLayer(layerId)) {
      addOverlayLayer(
        this.map,
        {
          id: layerId,
          type: 'raster',
          source: sourceId,
          paint: {
            'raster-opacity': opacity,
            'raster-opacity-transition': { duration: 0, delay: 0 },
            'raster-fade-duration': 0,
          },
        },
        'sun-exposure'
      )
    }
  }

  private removeBuffer(index: number): void {
    if (!this.map) {
      return
    }
    const { sourceId, layerId } = BUFFERS[index]
    if (this.map.getLayer(layerId)) {
      this.map.removeLayer(layerId)
    }
    if (this.map.getSource(sourceId)) {
      this.map.removeSource(sourceId)
    }
  }

  private onTimelineChange = (_state: Readonly<TimelineState>): void => {
    if (!this._enabled || !this.map) {
      return
    }

    const quantized = quantizeTime(_state.currentTime.getTime())
    if (quantized === this.currentTimeMs) {
      return
    }

    this.currentTimeMs = quantized
    this.scheduleRefresh()
  }

  private scheduleRefresh(): void {
    const now = performance.now()
    const elapsed = now - this.lastRefreshTime

    if (elapsed >= SunExposureService.REFRESH_INTERVAL_MS) {
      this.doRefresh()
    } else {
      if (this.pendingRefreshTimer !== null) {
        clearTimeout(this.pendingRefreshTimer)
      }
      const delay = SunExposureService.REFRESH_INTERVAL_MS - elapsed
      this.pendingRefreshTimer = setTimeout(() => {
        this.pendingRefreshTimer = null
        if (this._enabled && this.map) {
          this.doRefresh()
        }
      }, delay)
    }
  }

  private refreshStartTime = 0

  private doRefresh(): void {
    if (!this.map || !this._enabled) {
      return
    }
    this.lastRefreshTime = performance.now()
    this.refreshStartTime = performance.now()

    const backIndex = 1 - this.frontBuffer
    this.refreshGeneration++

    // Remove old back buffer if it exists, then add with new time
    this.removeBuffer(backIndex)
    // Add back buffer invisibly (opacity 0)
    this.addBuffer(backIndex, this.currentTimeMs, 0)
    this.backBufferLoading = true
    this.awaitBufferLoaded(backIndex, () => this.trySwap())
    log.debug('refresh started, waiting for tiles...')
  }

  private awaitBufferLoaded(bufferIndex: number, onLoaded: () => void): void {
    if (!this.map) return
    if (this.sourceLoadHandler) {
      this.map.off('sourcedata', this.sourceLoadHandler)
      this.sourceLoadHandler = null
    }
    const sourceId = BUFFERS[bufferIndex].sourceId
    const handler = (e: { sourceId: string }): void => {
      if (e.sourceId !== sourceId || !this.map) return
      if (!this.map.isSourceLoaded(sourceId)) return
      this.map.off('sourcedata', handler)
      if (this.sourceLoadHandler === handler) this.sourceLoadHandler = null
      onLoaded()
    }
    this.sourceLoadHandler = handler
    this.map.on('sourcedata', handler)
  }

  private trySwap(): void {
    if (!this.backBufferLoading || !this.map || !this._enabled) {
      return
    }

    this.backBufferLoading = false

    const swapMs = performance.now() - this.refreshStartTime
    log.debug(`buffer swap complete in ${swapMs.toFixed(0)}ms`)

    const oldFront = this.frontBuffer
    const backIndex = 1 - oldFront
    const back = BUFFERS[backIndex]

    if (this.map.getLayer(back.layerId)) {
      this.map.setPaintProperty(back.layerId, 'raster-opacity', this._opacity)
    }
    this.frontBuffer = backIndex

    // Defer removal of the old buffer until MapLibre has painted a frame
    // with the new buffer visible, preventing a flash of empty space.
    // The generation check prevents a stale callback from removing a buffer
    // that doRefresh has already recycled for the next back buffer.
    const gen = this.refreshGeneration
    this.map.once('render', () => {
      if (gen !== this.refreshGeneration) return
      this.removeBuffer(oldFront)
    })
  }

  private async fetchDemTile(
    z: number,
    x: number,
    y: number,
    signal: AbortSignal
  ): Promise<Uint8ClampedArray> {
    const pixels = await demTileCache.getTile(z, x, y, signal)
    if (!pixels) throw new Error(`DEM fetch failed: ${z}/${x}/${y}`)
    return pixels
  }

  // Fetch 3x3 neighborhood and composite into a single padded buffer
  private async fetchPaddedDem(
    z: number,
    x: number,
    y: number,
    signal: AbortSignal
  ): Promise<Uint8ClampedArray> {
    const maxTile = Math.pow(2, z) - 1
    const padded = new Uint8ClampedArray(PADDED_SIZE * PADDED_SIZE * 4)

    // Fetch all 9 tiles in parallel
    const fetches: Promise<{ dx: number; dy: number; pixels: Uint8ClampedArray | null }>[] = []
    for (let dy = -1; dy <= 1; dy++) {
      for (let dx = -1; dx <= 1; dx++) {
        const nx = x + dx
        const ny = y + dy
        if (ny < 0 || ny > maxTile) {
          fetches.push(Promise.resolve({ dx, dy, pixels: null }))
        } else {
          const wrappedX = ((nx % (maxTile + 1)) + (maxTile + 1)) % (maxTile + 1)
          fetches.push(
            this.fetchDemTile(z, wrappedX, ny, signal)
              .then(pixels => ({ dx, dy, pixels }))
              .catch(() => ({ dx, dy, pixels: null }))
          )
        }
      }
    }

    const results = await Promise.all(fetches)

    // First pass: copy available tiles into the padded buffer
    let centerPixels: Uint8ClampedArray | null = null
    for (const { dx, dy, pixels } of results) {
      if (!pixels) {
        continue
      }
      if (dx === 0 && dy === 0) {
        centerPixels = pixels
      }
      const offsetX = (dx + 1) * TILE_SIZE
      const offsetY = (dy + 1) * TILE_SIZE

      for (let row = 0; row < TILE_SIZE; row++) {
        const srcStart = row * TILE_SIZE * 4
        const dstStart = ((offsetY + row) * PADDED_SIZE + offsetX) * 4
        padded.set(pixels.subarray(srcStart, srcStart + TILE_SIZE * 4), dstStart)
      }
    }

    // Second pass: fill missing neighbor tiles by clamping to center tile edges
    // This avoids the -32768m elevation cliff from zero-filled regions
    if (centerPixels) {
      for (const { dx, dy, pixels } of results) {
        if (pixels) {
          continue
        } // already filled
        const offsetX = (dx + 1) * TILE_SIZE
        const offsetY = (dy + 1) * TILE_SIZE

        for (let row = 0; row < TILE_SIZE; row++) {
          // Clamp source coordinates to center tile edges
          const srcRow = Math.max(0, Math.min(TILE_SIZE - 1, row + dy * TILE_SIZE))
          for (let col = 0; col < TILE_SIZE; col++) {
            const srcCol = Math.max(0, Math.min(TILE_SIZE - 1, col + dx * TILE_SIZE))
            const srcIdx = (srcRow * TILE_SIZE + srcCol) * 4
            const dstIdx = ((offsetY + row) * PADDED_SIZE + offsetX + col) * 4
            padded[dstIdx] = centerPixels[srcIdx]
            padded[dstIdx + 1] = centerPixels[srcIdx + 1]
            padded[dstIdx + 2] = centerPixels[srcIdx + 2]
            padded[dstIdx + 3] = centerPixels[srcIdx + 3]
          }
        }
      }
    }

    return padded
  }

  private async handleTileRequest(
    params: { url: string },
    abortController: AbortController
  ): Promise<{ data: ImageBitmap }> {
    const match = params.url.match(/(\d+)\/(\d+)\/(\d+)\?t=(\d+)&m=(\w+)/)
    if (!match) {
      throw new Error(`Invalid sun tile URL: ${params.url}`)
    }

    const z = parseInt(match[1])
    const x = parseInt(match[2])
    const y = parseInt(match[3])
    const timeMs = parseInt(match[4])
    const tileMode = match[5] as SunExposureMode
    const key = `${z}/${x}/${y}/${timeMs}/${tileMode}`

    this.pendingRequests.set(key, abortController)

    try {
      const tTotal = performance.now()

      // Fetch 3x3 padded DEM neighborhood
      const tDem = performance.now()
      const paddedDem = await this.fetchPaddedDem(z, x, y, abortController.signal)
      const demMs = performance.now() - tDem

      // Compute sun position from the timestamp encoded in the tile URL
      const center = tileCenterLatLng(x, y, z)
      const tileTime = new Date(timeMs)
      const sunPos = SunCalc.getPosition(tileTime, center.lat, center.lng)
      const sunAzimuth = sunPos.azimuth + Math.PI // Convert from south to north
      const sunAltitude = sunPos.altitude

      const tRender = performance.now()
      const gpuResult = renderSunExposureTileGPU(
        paddedDem,
        PADDED_SIZE,
        TILE_SIZE,
        z,
        y,
        sunAzimuth,
        sunAltitude,
        tileMode
      )
      const pixels =
        gpuResult ??
        (await this.processInWorkerWithInit(
          paddedDem,
          PADDED_SIZE,
          TILE_SIZE,
          z,
          y,
          sunAzimuth,
          sunAltitude,
          tileMode,
          abortController.signal
        ))
      const renderMs = performance.now() - tRender

      // Hand the decoded RGBA straight to MapLibre as an ImageBitmap. MapLibre v5 uses a
      // protocol response whose `data` is already an ImageBitmap as-is (no image decode),
      // which avoids any encoded-image container. This matters because Chromium's BMP decoder
      // ignores the alpha channel (rendering transparent tiles as solid black), and PNG would
      // add encode cost — createImageBitmap is native, off-thread, and carries true alpha.
      // premultiplyAlpha: 'none' is required: MapLibre uploads the ImageBitmap with
      // UNPACK_PREMULTIPLY_ALPHA=true (it premultiplies during texImage2D), so the source
      // must carry STRAIGHT alpha. (Matches slope-angle-service.)
      const tEncode = performance.now()
      const bitmap = await createImageBitmap(
        new ImageData(pixels as Uint8ClampedArray<ArrayBuffer>, TILE_SIZE, TILE_SIZE),
        { premultiplyAlpha: 'none' }
      )
      const encodeMs = performance.now() - tEncode

      const totalMs = performance.now() - tTotal
      log.debug(
        `tile ${z}/${x}/${y} total=${totalMs.toFixed(1)}ms dem=${demMs.toFixed(1)}ms render=${renderMs.toFixed(1)}ms(${gpuResult ? 'gpu' : 'cpu'}) encode=${encodeMs.toFixed(1)}ms`
      )

      return { data: bitmap }
    } catch (e) {
      if (e instanceof DOMException && e.name === 'AbortError') throw e
      log.warn(`tile ${z}/${x}/${y} failed:`, e)
      return {
        data: await createImageBitmap(new ImageData(TILE_SIZE, TILE_SIZE), {
          premultiplyAlpha: 'none',
        }),
      }
    } finally {
      this.pendingRequests.delete(key)
    }
  }

  private processInWorkerWithInit(
    demPixels: Uint8ClampedArray,
    demWidth: number,
    padding: number,
    z: number,
    y: number,
    sunAzimuth: number,
    sunAltitude: number,
    mode: SunExposureMode,
    signal: AbortSignal
  ): Promise<Uint8ClampedArray> {
    if (this.workers.length === 0) {
      log.info('GPU unavailable, falling back to CPU workers')
    }
    this.initWorkers()
    return this.processInWorker(
      demPixels,
      demWidth,
      padding,
      z,
      y,
      sunAzimuth,
      sunAltitude,
      mode,
      signal
    )
  }

  private processInWorker(
    demPixels: Uint8ClampedArray,
    demWidth: number,
    padding: number,
    z: number,
    y: number,
    sunAzimuth: number,
    sunAltitude: number,
    mode: SunExposureMode,
    signal: AbortSignal
  ): Promise<Uint8ClampedArray> {
    return new Promise((resolve, reject) => {
      if (signal.aborted) {
        reject(new DOMException('Aborted', 'AbortError'))
        return
      }

      const requestId = this.nextRequestId++
      const worker = this.getWorker()
      const handler = (e: MessageEvent): void => {
        if (e.data.requestId !== requestId) {
          return
        }
        worker.removeEventListener('message', handler)
        signal.removeEventListener('abort', onAbort)
        resolve(new Uint8ClampedArray(e.data.pixels))
      }
      const onAbort = (): void => {
        worker.removeEventListener('message', handler)
        reject(new DOMException('Aborted', 'AbortError'))
      }

      worker.addEventListener('message', handler)
      signal.addEventListener('abort', onAbort)
      worker.postMessage({
        demPixels,
        demWidth,
        padding,
        z,
        y,
        sunAzimuth,
        sunAltitude,
        mode,
        requestId,
      })
    })
  }
}

export const sunExposureService = new SunExposureService()
